import styled, { keyframes } from 'styled-components';
import { GRUNDFOS_BLUE, BLUE2, BLUE3, BLUE4, BLUE1 } from '../../../constants/colors';
import { FONT_FAMILY, FONT_SIZE, FONT_WEIGHT } from '../../../constants/fonts';
import { MEDIA } from '../../../constants/breakpoints';

const bounce = keyframes`
  0%, 80%, 100% { transform: scale(0); }
  40%            { transform: scale(1); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const ChatMessage = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 82%;
  align-self: ${props => props.$isUser ? 'flex-end' : 'flex-start'};
  animation: ${fadeIn} 0.25s ease both;

  ${MEDIA.MOBILE} {
    max-width: 90%;
  }
`;

export const ChatMessage__Bubble = styled.div<{ $isUser: boolean; $isLoading: boolean }>`
  padding: 12px 16px;
  border-radius: 14px;
  word-wrap: break-word;
  font-family: ${FONT_FAMILY.SEMI_LIGHT};
  font-weight: ${FONT_WEIGHT.REGULAR};
  font-size: ${FONT_SIZE.MEDIUM};
  line-height: 1.55;

  ${props => props.$isLoading && `
    background: white;
    border: 1.5px solid ${BLUE2};
    color: ${BLUE3};
  `}

  ${props => !props.$isLoading && props.$isUser && `
    background: ${GRUNDFOS_BLUE};
    color: #ffffff;
    border-bottom-right-radius: 4px;
  `}

  ${props => !props.$isLoading && !props.$isUser && `
    background: white;
    color: ${BLUE4};
    border: 1.5px solid ${BLUE2};
    border-bottom-left-radius: 4px;
  `}
`;

export const ChatMessage__Timestamp = styled.div<{ $isUser: boolean }>`
  font-size: 11px;
  color: ${BLUE3};
  font-family: ${FONT_FAMILY.REGULAR};
  font-weight: ${FONT_WEIGHT.LIGHT};
  padding: 0 4px;
  text-align: ${props => props.$isUser ? 'right' : 'left'};
  opacity: 0.75;
`;

export const ChatMessage__LoadingDots = styled.div`
  display: inline-flex;
  gap: 5px;
  padding: 2px 0;
`;

export const ChatMessage__Dot = styled.div<{ $delay: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${BLUE3};
  animation: ${bounce} 1.4s infinite ease-in-out both;
  animation-delay: ${props => props.$delay};
`;

export const ChatMessage__Link = styled.a`
  color: inherit;
  text-decoration: underline;
  word-break: break-all;

  &:hover {
    opacity: 0.75;
  }
`;

export const ChatMessage__MarkdownWrapper = styled.div`
  & > *:first-child { margin-top: 0; }
  & > *:last-child  { margin-bottom: 0; }

  p {
    margin: 0.4em 0;
    white-space: pre-wrap;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 0.6em 0;
    font-size: 0.9em;
    overflow-x: auto;
    display: block;
  }

  thead { background: ${BLUE1}; }

  th, td {
    border: 1px solid ${BLUE2};
    padding: 6px 10px;
    text-align: left;
    white-space: nowrap;
  }

  th {
    font-weight: 600;
    color: ${GRUNDFOS_BLUE};
  }

  tr:nth-child(even) { background: ${BLUE1}; }

  ul, ol {
    margin: 0.4em 0 0.4em 1.4em;
    padding: 0;
  }

  li { margin: 0.15em 0; }

  code {
    background: ${BLUE1};
    padding: 0.15em 0.35em;
    border-radius: 3px;
    font-size: 0.9em;
  }

  pre {
    background: ${BLUE1};
    padding: 0.6em 0.8em;
    border-radius: 6px;
    overflow-x: auto;
    margin: 0.5em 0;

    code {
      background: none;
      padding: 0;
    }
  }

  h1, h2, h3, h4, h5, h6 {
    margin: 0.6em 0 0.3em;
    line-height: 1.3;
  }

  blockquote {
    border-left: 3px solid ${BLUE2};
    margin: 0.4em 0;
    padding: 0.2em 0.8em;
    color: ${BLUE3};
  }

  hr {
    border: none;
    border-top: 1px solid ${BLUE2};
    margin: 0.6em 0;
  }

  strong { font-weight: 600; }
  em     { font-style: italic; }
`;
